import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "universal-cookie";
import { USER_API } from "../../common/common";
import { Logout } from "../../common/Funcation";
import { notifyError, notifyMessage, postApi, sendEvent } from "../../service";
import { liveKey, oddsHoldText } from "./useInPlayOdds";

const cookies = new Cookies();
const BACK = ["b1", "b2", "b3"];
const LAY = ["l1", "l2", "l3"];

const asNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};
const sameId = (a, b) => String(a ?? "") !== "" && String(a) === String(b);

const secondField = (first) =>
  ({ b1: "bs1", b2: "bs2", b3: "bs3", l1: "ls1", l2: "ls2", l3: "ls3" }[first] || "bs1");

function selectionId(runner) {
  const n = Number(runner?.sId);
  return Number.isFinite(n) ? n : null;
}

function holdText(live, runner) {
  if (!live) return "";
  if (live.suspended) return "Suspended";
  return oddsHoldText(runner?.status);
}

function sideOverMax(limit, price) {
  const max = Number(limit?.maxPrice) || 0;
  if (max <= 0) return false;
  const p = Number(price);
  return Number.isFinite(p) && p > max;
}

function priceAllowed(limit, price) {
  const p = Number(price);
  if (!Number.isFinite(p) || p <= 0) return false;
  if (!limit || limit.min == null || limit.max == null) return true;
  return Number(limit.min) <= p && Number(limit.max) >= p;
}

function stakeLimitMessage(limit, stake) {
  const min = Number(limit?.min);
  const max = Number(limit?.max);
  if (Number.isFinite(min) && min > 0 && stake < min) return `Minimum bet limit is ${min}!`;
  if (Number.isFinite(max) && max > 0 && stake > max) return `Maximum bet limit is ${max}!`;
  return "";
}

// Scroll the clicked odds row so it sits ~60px below the top of the viewport,
// so the BetBox never covers it. Same behaviour as MultiMarket.
const OFFSET_FROM_TOP = 160;

function scrollToOddsElement(el) {
  if (!el || !el.isConnected) return;
  // use the runner row (li) as the target so the runner name stays visible
  const target = el.closest("li") || el.closest(".position-relative") || el;
  const rect = target.getBoundingClientRect();
  window.scrollBy({ top: rect.top - OFFSET_FROM_TOP, behavior: "smooth" });
}

/**
 * Bet state + placing for the odds market (t1) of IN-PLAY matches only.
 * `getLiveMatch(key)` must return the latest in-play entry (from useInPlayOdds).
 */
export default function useInPlayBetting(getLiveMatch) {
  const dispatch = useDispatch();
  const HeaderData = useSelector((e) => e.Header);

  const [clickedBet, setClickedBet] = useState({});
  const [clickedTableData, setClickedTableData] = useState("");
  const [clickedTable, setClickedTable] = useState("");
  const [placeBetLoader, setPlaceBetLoader] = useState(false);
  const betMatchRef = useRef(null);

  const resetCalc = () =>
    dispatch({
      type: "GET_SPORTS_LIVE_CALC",
      payload: { calcOntime: false, calcData: {} },
    });

  const resetBet = () => {
    setClickedBet({});
    setClickedTableData("");
    setClickedTable("");
    resetCalc();
  };

  // bet history feeds BetBox / exposure
  const loadBetHistory = async () => {
    try {
      const response = await postApi({ api: USER_API.BET_LIST, value: {} });
      dispatch({ type: "USER_BET_HISTORY", payload: response.data.data });
    } catch (err) {
      if (err?.response?.data?.statusCode === 401) Logout();
    }
  };

  useEffect(() => {
    if (cookies.get("skyTokenFront")) loadBetHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Call from the odds cell onClick.
   * item = list item, sportType = 'cricket' | 'soccer' | 'tennis', side = 'b1' | 'l1'
   */
  const openBet = (e, runner, side, item, sportType) => {
    e.stopPropagation(); // parent row navigates to the match page on click
    const clickedEl = e.currentTarget;            // <-- capture now
    const price = side === "l1" ? runner?.l1 : runner?.b1;
    const live = getLiveMatch(liveKey(item));
    // not in the latest in-play payload => not an in-play match => no bet
    if (!live) return;
    const hold = holdText(live, runner);
    if (hold) {
      notifyError(hold === "Ball Running" ? "Ball Running" : "Market suspended.");
      return;
    }
    if (selectionId(runner) == null) return;
    if (!(Number(price) > 0)) return;
    if (sideOverMax(live.bet_odds_limit, price)) {
      notifyError("Odds Limit Exceed!");
      return;
    }
    if (!cookies.get("skyTokenFront")) {
      window.location.href = "/login";
      return;
    }

    resetBet();
    betMatchRef.current = {
      _id: live._id || item._id,
      type: live.type || sportType,
      gameId: live.gameId,
      marketId: live.marketId,
    };
    setClickedBet(runner);
    setClickedTableData(side);
    setClickedTable("t1");

    // let the BetBox mount first, then scroll the cell up
    setTimeout(() => scrollToOddsElement(clickedEl), 50);
  };

  /**
   * Passed to <BetBox placeBetClicked={placeBet} />. Same signature as
   * MultiMarket's PlaceBat, trimmed to the odds market.
   */
  const placeBet = async (
    ITEM,
    TotalAmount,
    SelectedAmount,
    TABLE,
    TABLE_DATA,
    retundData = false
  ) => {
    const match = betMatchRef.current;
    if (!match || TABLE !== "t1") return undefined;

    const live = getLiveMatch(`${match.gameId}_${match.marketId}`);
    if (!live) {
      if (!retundData) notifyError("This match is no longer in-play.");
      return undefined;
    }

    let loaderTimer = null;
    if (!retundData) {
      setPlaceBetLoader(true);
      loaderTimer = setTimeout(() => setPlaceBetLoader(false), 5000);
    }

    // always bet on the freshest odds for this runner
    const runners = Array.isArray(live.t1) ? live.t1 : [];
    const runner = runners.find((r) => sameId(r?.sId, ITEM?.sId)) || ITEM;
    const sId = selectionId(runner);
    const second = secondField(TABLE_DATA);
    const stake = asNumber(SelectedAmount);
    const profit = asNumber(parseFloat(TotalAmount));
    const oddsUp = asNumber(runner?.[TABLE_DATA], asNumber(ITEM?.[TABLE_DATA]));

    const value = {
      type: match.type,
      matchId: match._id,
      betType: "odds",
      betSide: BACK.includes(TABLE_DATA) ? "back" : LAY.includes(TABLE_DATA) ? "lay" : undefined,
      selection: runner?.nat,
      betPlaced: stake,
      stake,
      oddsUp,
      oddsDown: asNumber(runner?.[second], asNumber(ITEM?.[second])),
      profit,
      exposure: LAY.includes(TABLE_DATA) ? profit : stake,
      winnerSelection: runners.map((r) => r.nat),
      sId: sId == null ? "" : sId,
      pId: "",
      position: TABLE_DATA,
      domain: window.location.hostname,
    };

    if (retundData) {
      // BetBox asks for fresh odds before submitting; the poll already provides them
      sendEvent("GET_INPLAY_MATCHES", { domain: window.location.hostname });
      return value;
    }

    const stop = (message) => {
      if (loaderTimer) clearTimeout(loaderTimer);
      notifyError(message);
      setPlaceBetLoader(false);
      return undefined;
    };

    const hold = holdText(live, runner);
    if (hold) return stop(hold === "Ball Running" ? "Ball Running" : "Market suspended.");
    if (sId == null) return stop("This match is no longer in-play.");
    if (sideOverMax(live.bet_odds_limit, oddsUp) || !priceAllowed(live.oddsLimit, oddsUp)) {
      return stop("Odds Limit Exceed!");
    }
    const stakeMessage = stakeLimitMessage(live.bet_odds_limit, stake);
    if (stakeMessage) return stop(stakeMessage);

    try {
      const response = await postApi({ api: USER_API.PLACE_BET, value });
      if (loaderTimer) clearTimeout(loaderTimer);

      const wallet = response?.data?.data;
      if (wallet && (wallet.balance !== undefined || wallet.exposure !== undefined)) {
        dispatch({
          type: "UPDATE_USER_BALANCE",
          payload: {
            balance: wallet.balance !== undefined ? Number(wallet.balance).toFixed(2) : undefined,
            exposure:
              wallet.exposure !== undefined ? Number(wallet.exposure).toFixed(2) : undefined,
            remaining_balance:
              wallet.remaining_balance !== undefined
                ? Number(wallet.remaining_balance).toFixed(2)
                : undefined,
          },
        });
      }
      notifyMessage("Bet Placed");
      loadBetHistory();
      setTimeout(() => sendEvent("UPDATE_USER_BALANCE", { userId: HeaderData?._id }), 500);
      setTimeout(() => sendEvent("UPDATE_USER_BALANCE", { userId: HeaderData?._id }), 1500);
      resetBet();
    } catch (err) {
      if (loaderTimer) clearTimeout(loaderTimer);
      notifyError(err?.response?.data?.message || "Bet placement failed");
      if (err?.response?.data?.statusCode === 401) Logout();
    } finally {
      if (loaderTimer) clearTimeout(loaderTimer);
      setPlaceBetLoader(false);
    }
    return undefined;
  };

  return {
    clickedBet,
    clickedTable,
    clickedTableData,
    placeBetLoader,
    setPlaceBetLoader,
    openBet,
    placeBet,
    resetBet,
  };
}
